import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { classNames } from "@/lib/format";
import { getFernausleseChannels } from "@/lib/fernausleseApi";
import { fetchInfoScreenEventsConfig } from "@/lib/infoScreenEventsApi";
import { getIntegrationsStatus } from "@/lib/integrationsStatusApi";
import { getTouristImportStats } from "@/lib/touristImportApi";
import {
  buildIntegrationTileStatuses,
  type IntegrationTileStatus,
} from "@/pages/stammdaten/schnittstellen/buildIntegrationTileStatuses";
import {
  SCHNITTSTELLEN_INTEGRATIONS,
  schnittstellenIntegrationHref,
  type SchnittstellenIntegrationId,
} from "@/pages/stammdaten/schnittstellenIntegrations";

export function SchnittstellenOverview() {
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Record<SchnittstellenIntegrationId, IntegrationTileStatus>>(
    {} as Record<SchnittstellenIntegrationId, IntegrationTileStatus>,
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getIntegrationsStatus();
      setStatuses(buildIntegrationTileStatuses(settings, null, null, null));
      setLoading(false);

      const [eventsConfig, touristImportStats, fernausleseChannels] = await Promise.all([
        fetchInfoScreenEventsConfig().catch(() => null),
        getTouristImportStats({ includeReconcile: false, includeDiagnostics: false }).catch(() => null),
        getFernausleseChannels().catch(() => null),
      ]);
      setStatuses(
        buildIntegrationTileStatuses(settings, eventsConfig, touristImportStats, fernausleseChannels),
      );
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p className="text-sm text-label-secondary">Status wird geladen…</p>;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm leading-relaxed text-label-secondary">
        Wählen Sie eine Anbindung, um sie einzurichten oder zu prüfen. „Aktiv“ bedeutet, dass die
        Schnittstelle konfiguriert ist; „Offen“ heißt, sie wird noch nicht genutzt.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {SCHNITTSTELLEN_INTEGRATIONS.map((integration) => {
          const status = statuses[integration.id];
          const Icon = integration.icon;
          const configured = status?.configured ?? false;

          return (
            <Link
              key={integration.id}
              to={schnittstellenIntegrationHref(integration.id)}
              className={classNames(
                "group relative flex flex-col rounded-2xl border border-separator bg-card p-4",
                "transition-all duration-200 hover:border-accent/30 hover:bg-fill-quaternary hover:shadow-sm",
                "active:scale-[0.99]",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <span
                  className={classNames(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105",
                    integration.accentClass,
                  )}
                  aria-hidden
                >
                  <Icon size={20} strokeWidth={1.75} className={integration.iconClass} />
                </span>
                <span
                  className={classNames(
                    "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                    configured
                      ? "bg-status-green/15 text-status-green"
                      : "border border-separator bg-fill-tertiary text-label-secondary",
                  )}
                >
                  {configured ? "Aktiv" : "Offen"}
                </span>
              </div>

              <p className="mt-3 text-sm font-semibold text-label">{integration.label}</p>
              <p className="mt-0.5 line-clamp-1 text-xs text-label-secondary">
                {status?.detail ?? "—"}
              </p>

              <ChevronRight
                size={16}
                className="absolute bottom-4 right-4 text-label-tertiary opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
